import React, { useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
  useParams,
} from 'react-router-dom';
import { DatabaseProvider, useDatabase } from './context/DatabaseContext';
import { ToastProvider } from './context/ToastContext';
import { AuthProvider } from './context/AuthContext';
import { Layout } from './components/layout/Layout';
import { DatabaseConnection } from './pages/DatabaseConnection';
import { Dashboard } from './pages/Dashboard';
import { SchemaAnalysis } from './pages/SchemaAnalysis';
import { InsightQuery } from './pages/InsightQuery';
import { QueryGenerator } from './pages/QueryGenerator';
import { ApiEndpoints } from './pages/ApiEndpoints';
import { Charts } from './pages/Charts';
import { AIAgentsList } from './pages/AIAgentsList';
import { AgentConfiguration } from './pages/AgentConfiguration';
import { AgentInsightsSelection } from './pages/AgentInsightsSelection';
import { SubAgentsView } from './pages/SubAgentsView';
import { SubAgentEdit } from './pages/SubAgentEdit';
import { SubAgentCreate } from './pages/SubAgentCreate';
import { SubAgentQueries } from './pages/SubAgentQueries';
import { AgentChat } from './pages/AgentChat';
import { QueryExecutionPage } from './pages/QueryExecutionPage';
import { QueryCompareView } from './pages/QueryCompareView';
import { InsightsAnalysisResults } from './pages/InsightsAnalysisResults';
import { ReportGeneration } from './pages/ReportGeneration';
import { ReportPrioritiesPage } from './pages/ReportPrioritiesPage';
import { ReportsLanding } from './pages/ReportsLanding';
import { ReportChat } from './pages/ReportChat';
import { PlansLanding } from './pages/PlansLanding';
import { PlanDetailPage } from './pages/PlanDetailPage';
import { DatabaseUnderstandingSettings } from './pages/DatabaseUnderstandingSettings';
import { ChartLibrary } from './pages/ChartLibrary';
import { ChartDetail } from './pages/ChartDetail';
import { Settings } from './pages/Settings';
import { SkillsList } from './pages/SkillsList';
import { SkillsView } from './pages/SkillsView';
import { SkillCreate } from './pages/SkillCreate';
import { SkillEdit } from './pages/SkillEdit';
import { Login } from './pages/Login';
import { ProtectedRoute } from './components/ProtectedRoute';

/** Old ``/skills/:id/create`` — skills must be created inside a subfolder. */
function SkillsLegacyCreateRedirect() {
  const { databaseId } = useParams();
  return <Navigate to={`/skills/${databaseId}`} replace />;
}

// Component to refresh data on route change
function RouteChangeHandler() {
  const location = useLocation();
  const { refreshDatabases } = useDatabase();

  useEffect(() => {
    const isAuthed =
      localStorage.getItem('isAuthenticated') === 'true' &&
      Boolean(localStorage.getItem('token'));

    if (!isAuthed) return;
    if (location.pathname === '/login') return;

    refreshDatabases();
  }, [location.pathname, refreshDatabases]);

  return null;
}

// Stable login route — reads auth only once on mount, preventing re-render-triggered navigation
function LoginRoute() {
  const [isAuthed] = useState(
    () =>
      localStorage.getItem('isAuthenticated') === 'true' &&
      Boolean(localStorage.getItem('token'))
  );
  return isAuthed ? <Navigate to="/dashboard" replace /> : <Login />;
}

function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <DatabaseProvider>
          <Router>
          <RouteChangeHandler />
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/chart-library/:id"
              element={
                <ProtectedRoute>
                  <ChartDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="*"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<Navigate to="/dashboard" replace />} />
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/database-connection" element={<DatabaseConnection />} />
                      <Route path="/schema-analysis" element={<SchemaAnalysis />} />
                      <Route path="/agents" element={<AIAgentsList />} />
                      <Route path="/agents/:databaseId" element={<SubAgentsView />} />
                      <Route path="/agents/chat/:databaseId" element={<AgentChat />} />
                      <Route path="/agents/config" element={<AgentConfiguration />} />
                      <Route path="/agents/insights/:agentId" element={<AgentInsightsSelection />} />
                      <Route path="/agents/insights/results" element={<InsightsAnalysisResults />} />
                      <Route path="/agents/insights/results/:analysisId" element={<InsightsAnalysisResults />} />
                      <Route path="/agents/insights/:agentId/results" element={<InsightsAnalysisResults />} />
                      <Route path="/agents/queries/:agentId" element={<SubAgentQueries />} />
                      <Route path="/agents/:agentId/execute/:queryId" element={<QueryExecutionPage />} />
                      <Route path="/agents/:agentId/compare/:queryId" element={<QueryCompareView />} />
                      <Route path="/agents/sub-agent/create" element={<SubAgentCreate />} />
                      <Route path="/agents/sub-agent/:agentId/edit" element={<SubAgentEdit />} />
                      <Route path="/insight-query" element={<InsightQuery />} />
                      <Route path="/query-generator" element={<QueryGenerator />} />
                      <Route path="/api-endpoints" element={<ApiEndpoints />} />
                      <Route path="/charts" element={<Charts />} />
                      <Route path="/reports" element={<ReportsLanding />} />
                      <Route path="/reports/:databaseId/priorities" element={<ReportPrioritiesPage />} />
                      <Route path="/reports/:databaseId/chat" element={<ReportChat />} />
                      <Route path="/reports/:databaseId" element={<ReportGeneration />} />
                      <Route path="/reports/:databaseId/plan" element={<PlanDetailPage />} />
                      <Route path="/plans" element={<PlansLanding />} />
                      <Route path="/plans/:planId" element={<PlanDetailPage />} />
                      <Route path="/database-understanding" element={<DatabaseUnderstandingSettings />} />
                      <Route path="/chart-library" element={<ChartLibrary />} />

                      <Route path="/skills" element={<SkillsList />} />
                      <Route path="/skills/:databaseId/folder/:folderSlug/create" element={<SkillCreate />} />
                      <Route path="/skills/:databaseId/folder/:folderSlug" element={<SkillsView />} />
                      <Route path="/skills/:databaseId/create" element={<SkillsLegacyCreateRedirect />} />
                      <Route path="/skills/:databaseId" element={<SkillsView />} />
                      <Route path="/skills/:databaseId/edit/:skillId" element={<SkillEdit />} />
                    </Routes>
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
          </Router>
        </DatabaseProvider>
      </AuthProvider>
    </ToastProvider>
  );
}

export default App;
